import { useRef, useState } from "react";
import { deriveKeys, type KdfParams } from "../crypto";
import { api, errMsg } from "../lib/api";
import { Button, Field, Input } from "../ui/ds";
import { Icon } from "../ui/Icon";
import { useToast, type SessionInfo } from "../state/store";
import { GateCard } from "./GateCard";
import { openSession, rememberEmail, rememberedEmail } from "./session";

type Step = "pw" | "2fa" | "backup";

/**
 * Unlock: email + master password (checked by the server via the derived auth
 * key), then an authenticator or backup code. The stretched key derived from
 * the password stays in memory and unlocks the account once both pass.
 */
export function Unlock({ reason, onUnlocked }: { reason?: string; onUnlocked: (s: SessionInfo) => void }) {
  const toast = useToast();
  const [email, setEmail] = useState(rememberedEmail());
  const [editingEmail, setEditingEmail] = useState(!rememberedEmail());
  const [step, setStep] = useState<Step>("pw");
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [bcode, setBcode] = useState("");
  const [err, setErr] = useState(reason ?? "");
  const [busy, setBusy] = useState(false);
  const pending = useRef<{ token: string; stretched: CryptoKey } | null>(null);

  const submitPw = async () => {
    const addr = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) return setErr("Enter a valid email address.");
    if (!pw) return setErr("Enter your master password.");
    setBusy(true);
    setErr("");
    try {
      const pre = await api<{ kdf: KdfParams; kdfSalt: string }>("/auth/prelogin", { body: { email: addr } });
      const { stretched, authKeyB64 } = await deriveKeys(pw, pre.kdfSalt, pre.kdf);
      const res = await api<{ pending: string }>("/auth/login", { body: { email: addr, authKey: authKeyB64 } });
      pending.current = { token: res.pending, stretched };
      rememberEmail(addr);
      setEmail(addr);
      setEditingEmail(false);
      setPw("");
      setStep("2fa");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (kind: "code" | "backup") => {
    if (kind === "code" && code.replace(/\D/g, "").length !== 6) return setErr("The code is 6 digits. Check your authenticator app.");
    if (kind === "backup" && !/^\d{4}-?\d{4}$/.test(bcode.trim())) return setErr("Backup codes look like 0000-0000.");
    if (!pending.current) return setStep("pw");
    setBusy(true);
    setErr("");
    try {
      const res = await api<{ ok: true; backupLeft?: number }>("/auth/verify", {
        body: kind === "code" ? { pending: pending.current.token, code } : { pending: pending.current.token, backupCode: bcode.trim() },
      });
      const session = await openSession(pending.current.stretched);
      pending.current = null;
      onUnlocked(session);
      if (res.backupLeft !== undefined)
        toast("Backup code used", `${res.backupLeft} left. Generate new ones in Settings when you run low.`, "info");
    } catch (e) {
      const msg = errMsg(e);
      setErr(msg);
      if (/password again/i.test(msg)) {
        pending.current = null;
        setStep("pw");
      }
    } finally {
      setBusy(false);
    }
  };

  const title = step === "pw" ? "Unlock your vault" : step === "2fa" ? "Two-step verification" : "Use a backup code";
  const sub =
    step === "pw"
      ? "Your vaults are encrypted on this device. Nobody else, including us, can open them."
      : step === "2fa"
        ? "Enter the 6-digit code from your authenticator app."
        : "Each code works once. Enter one of the 8-digit codes you saved.";

  return (
    <GateCard eyebrow="Agile Vault" title={title} sub={sub} foot="End-to-end encrypted · two-step verification required">
      {step === "pw" ? (
        <form
          className="gate-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void submitPw();
          }}
        >
          {editingEmail ? (
            <Field label="Email" htmlFor="email">
              <Input id="email" type="email" autoComplete="username" icon="user-round" value={email} onChange={(v) => { setEmail(v); setErr(""); }} placeholder="you@company.co.uk" />
            </Field>
          ) : (
            <div className="email-chip">
              <Icon name="user-round" size={16} />
              <span>{email}</span>
              <button type="button" className="linklike is-muted" onClick={() => { setEditingEmail(true); rememberEmail(null); }}>
                Not you?
              </button>
            </div>
          )}
          <Field label="Master password" htmlFor="pw">
            <Input
              id="pw"
              type="password"
              autoComplete="current-password"
              placeholder="Enter your master password"
              value={pw}
              autoFocus={!editingEmail}
              onChange={(v) => {
                setPw(v);
                setErr("");
              }}
            />
          </Field>
          <Button type="submit" fullWidth arrow busy={busy}>
            {busy ? "Unlocking…" : "Unlock"}
          </Button>
        </form>
      ) : step === "2fa" ? (
        <form
          className="gate-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void verify("code");
          }}
        >
          <Field label="Authenticator code" htmlFor="code">
            <Input
              id="code"
              size="lg"
              mono
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              placeholder="000 000"
              value={code}
              onChange={(v) => {
                setCode(v.replace(/[^\d ]/g, "").slice(0, 7));
                setErr("");
              }}
            />
          </Field>
          <Button type="submit" fullWidth arrow busy={busy}>
            Verify
          </Button>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <button type="button" className="linklike" onClick={() => { setStep("backup"); setErr(""); }}>
              Use a backup code
            </button>
            <button type="button" className="linklike is-muted" onClick={() => { setStep("pw"); setErr(""); pending.current = null; }}>
              Back
            </button>
          </div>
        </form>
      ) : (
        <form
          className="gate-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void verify("backup");
          }}
        >
          <Field label="Backup code" htmlFor="bcode">
            <Input id="bcode" size="lg" mono autoFocus placeholder="0000-0000" value={bcode} onChange={(v) => { setBcode(v.slice(0, 9)); setErr(""); }} />
          </Field>
          <Button type="submit" fullWidth arrow busy={busy}>
            Verify
          </Button>
          <button type="button" className="linklike" onClick={() => { setStep("2fa"); setErr(""); }}>
            Use authenticator code instead
          </button>
        </form>
      )}
      {err ? (
        <div className="gate-error" role="alert">
          {err}
        </div>
      ) : null}
    </GateCard>
  );
}
